import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle, Typography
} from "@mui/material";
import React from "react";
import { usePagesTranslation } from "../locales";

/**
 * CancelSubDialog Component
 * 
 * A confirmation dialog for canceling user subscription.
 * Displays a message asking for confirmation and provides support contact information.
 * 
 * @param {boolean} open - Controls the visibility of the dialog
 * @param {function} onClose - Callback function when dialog is closed
 * @param {function} onConfirm - Callback function when user confirms cancellation
 * @param {boolean} cancelling - Indicates if cancellation is in progress
 * @param {number|string} endDate - The subscription end date (timestamp or date string)
 */
const CancelSubDialog = ({ open, onClose, onConfirm, cancelling, endDate }) => {
    const { t } = usePagesTranslation();

    // Format the end date
    const formatEndDate = (dateValue) => {
        if (!dateValue) return 'N/A';
        try {
            const date = typeof dateValue === 'number' 
                ? new Date(dateValue) 
                : new Date(dateValue);
            
            if (isNaN(date.getTime())) {
                return 'N/A';
            }
            
            return date.toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
            });
        } catch (error) {
            return 'N/A';
        }
    };

    const formattedEndDate = formatEndDate(endDate);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>
                {t("profile.cancelSub.title")}
            </DialogTitle>
            <DialogContent>
                <Typography variant="body1" paragraph>
                    {t("profile.cancelSub.confirm-msg")}
                </Typography>
                {endDate && formattedEndDate !== 'N/A' && (
                    <Typography variant="body2" color="text.secondary" paragraph>
                        {t("profile.cancelSub.active-until", { date: formattedEndDate })}
                    </Typography>
                )}
                <Typography variant="body2" color="text.secondary" paragraph>
                    {t("profile.cancelSub.free-after")}
                </Typography>
            </DialogContent>
            <DialogActions sx={{ p: 2, gap: 1 }}>
                <Button onClick={onClose} variant="outlined">
                    {t("profile.cancelSub.keep-subscription")}
                </Button>
                <Button
                    onClick={onConfirm}
                    variant="contained"
                    color="error"
                    disabled={cancelling}
                >
                    {cancelling ? t("profile.cancelSub.cancelling") : t("profile.cancelSub.yes-cancel")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CancelSubDialog;

